import { Injectable } from '@angular/core';
import { BullhornDataSourceApiService } from 'datasource-service-lib';
import { IEntityMetadata } from 'interfaces-lib';
import { IMultiselectListItem } from 'multi-select-autocomplete-lib';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

export type BhEntityFieldMetaData = Partial<{
  id: string;
  name: string;
  type: string;
  dataType: string;
  options: Array<{ value: any; label: string }>;
  optionsType: string;
  optionsUrl: string;
  maxLength: number;
  confidential: boolean;
  hideFromSearch: boolean;
  label: string;
}>;

@Injectable({ providedIn: 'root' })
export class BullhornEntityFiltersService {
  static readonly DIGIT_REGEX = /^\d+$/;
  static readonly COMMA_SEPARATED_DIGITS_REGEX = /^\d*(\s*,\s*\d*)+$/;

  public entity = ''; // override in subclass
  public entityPath: string[] = []; // override in subclass
  public percentageFields = []; // override in subclass

  protected unsupportedTypes = []; // override in subclass
  protected unsupportedDataTypes = []; // override in subclass
  protected relationshipFields = []; // override in subclass

  get relationshipFieldNames(): string[] {
    return this.relationshipFields.map((f) => f.name);
  }

  constructor(protected dataSourceApiService: BullhornDataSourceApiService) {}

  getQueryFunctionForField(field: string): (keyword: string) => Observable<IMultiselectListItem[]> {
    return this.relationshipFields.find((f) => f.name === field)?.queryFunction ?? (() => of([]));
  }

  getEntityFields(): Observable<BhEntityFieldMetaData[]> {
    return this.dataSourceApiService
      .meta({
        entity: this.entity,
        fields: ['*'],
      })
      .pipe(
        map((response) =>
          response.fields
            .filter((f) => !this.unsupportedTypes.includes(f.type))
            .filter((f) => !this.unsupportedDataTypes.includes(f.dataType))
            .map((f) => this.transformFieldMetadata(f, response))
        )
      );
  }

  protected buildQueryParams(keyword: string, additionalSearchFilters = '', additionalQueryFilters = '') {
    if (BullhornEntityFiltersService.DIGIT_REGEX.test(keyword)) {
      const id = parseInt(keyword, 10);
      return {
        query: `id: (${id} OR ${id}*) AND NOT status:("Archive") ${additionalSearchFilters}`.trim(),
        searchType: 'search',
      };
    } else if (BullhornEntityFiltersService.COMMA_SEPARATED_DIGITS_REGEX.test(keyword) && keyword.includes(',')) {
      // search by comma-separated list of ids
      const ids = keyword
        .split(',')
        .map((id) => id.trim())
        .filter((id) => id && BullhornEntityFiltersService.DIGIT_REGEX.test(id)); // Filter out empty and non-numeric values

      // If we have valid IDs after filtering, use ID search, otherwise fall back to name search
      if (ids.length > 0) {
        const idQuery = ids.join(' OR ');
        return {
          query: `id: (${idQuery}) AND NOT status:("Archive") ${additionalSearchFilters}`.trim(),
          searchType: 'search',
        };
      } else {
        // No valid IDs found, fall back to name search
        return {
          query: `id > 0 AND name LIKE '%${keyword}%' AND status <> 'Archive' ${additionalQueryFilters}`.trim(),
          searchType: 'query',
        };
      }
    } else {
      // search by name
      return {
        query: `id > 0 AND name LIKE '%${keyword}%' AND status <> 'Archive' ${additionalQueryFilters}`.trim(),
        searchType: 'query',
      };
    }
  }

  protected executeQuery(
    entity: string,
    fields: string[], // e.g., ['id', 'name]
    keyword: string,
    additionalSearchFilters = '',
    additionalQueryFilters = ''
  ): Observable<IMultiselectListItem[]> {
    if (!keyword || !keyword.trim().length) {
      return of([]);
    }

    keyword = keyword.trim();
    const { query, searchType } = this.buildQueryParams(keyword, additionalSearchFilters, additionalQueryFilters);

    return this.dataSourceApiService.query2(entity, { searchType, query, fields: fields.join(',') }).pipe(
      map((response) => {
        return response.map((record) => ({
          id: record.id,
          label: record.name,
          data: record,
        }));
      })
    );
  }

  private transformFieldMetadata(field: any, metaResponse: IEntityMetadata): BhEntityFieldMetaData {
    const isIdField = field.type === 'ID';
    const displayName = isIdField ? `${metaResponse.label} (${metaResponse.name})` : `${field.label} (${field.name})`;
    return {
      ...field,
      id: field.name,
      name: displayName,
    };
  }
}
